import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type VisibilityState,
} from '@tanstack/react-table';
import { toast, cn } from '@noor/lib';
import {
  LegacyStatusBadge,
  legacyThousands,
  legacyListDateTime,
  legacyCourierName,
  type LegacyOrder,
} from '@/entities/legacy-order';
import { LegacyRouteCell } from './legacy-route-cell';
import { LegacyActionsCell } from './legacy-actions-cell';
import { LegacyIdCell } from './legacy-id-cell';
import { LegacyVendorOrderCell } from './legacy-vendor-order-cell';
import { LegacyWaitPriceCell } from './legacy-wait-price-cell';
import { LegacyProductPaidCell } from './legacy-product-paid-cell';

/**
 * The orders grid, reproducing the old panel's AG Grid Enterprise table on
 * TanStack Table.
 *
 * WHY NOT AG GRID. The old table used AG Grid Enterprise; carrying that
 * dependency (and its licence) into the new app for one legacy screen was
 * rejected in favour of `@tanstack/react-table`, which is already a
 * dependency. What that costs us is column resize/reorder/pinning — features
 * AG Grid gave for free. The old table's columns were `lockPinned` with fixed
 * widths and the only interactive affordance was the column-visibility
 * selector (reproduced through TanStack column visibility, driven from the
 * toolbar), so the loss is smaller than it sounds; widths below are the exact
 * pixel values measured off the live grid.
 *
 * LAYOUT. `table-fixed` with explicit `<col>` widths, not `flex: 1` — AG Grid
 * distributed leftover space across flexed columns, and the measured widths
 * already encode the result at the reference viewport. The row's rounded left
 * edge (20px) and 70px height come from the same measurement; the last column
 * is right-pinned in the original, reproduced here with `sticky right-0`.
 */
const columnHelper = createColumnHelper<LegacyOrder>();

/**
 * The columns, in the old grid's order — also what the toolbar's column
 * selector lists. `labelKey` is the header key, reused by the selector.
 */
export const LEGACY_ORDERS_COLUMNS: readonly { id: string; labelKey: string }[] = [
  { id: 'id', labelKey: 'table.id' },
  { id: 'vendor_order_id', labelKey: 'table.vendor_order_id' },
  { id: 'status', labelKey: 'table.status' },
  { id: 'route', labelKey: 'table.route' },
  { id: 'price', labelKey: 'table.price' },
  { id: 'waiting_price', labelKey: 'table.waiting_price' },
  { id: 'items', labelKey: 'table.items' },
  { id: 'product_paid', labelKey: 'table.product_paid' },
  { id: 'courier', labelKey: 'table.courier' },
  { id: 'date', labelKey: 'table.date' },
  { id: 'actions', labelKey: 'table.actions' },
];

/**
 * Column widths in px, measured off the live AG Grid. `route` is the flex
 * column — it absorbs the remaining width — so it gets a min rather than a
 * fixed size.
 */
const COLUMN_WIDTHS: Record<string, number> = {
  id: 119,
  vendor_order_id: 132,
  status: 180,
  route: 401,
  price: 132,
  waiting_price: 132,
  items: 130,
  product_paid: 202,
  courier: 201,
  date: 183,
  actions: 149,
};

interface LegacyOrdersTableProps {
  orders: LegacyOrder[];
  isLoading: boolean;
  /**
   * True on EVERY fetch, initial or background — drives the blur overlay the
   * old grid layered over the previous rows (`LoadingBlurry`).
   */
  isFetching: boolean;
  /** Opens the order drawer — the whole row is clickable except the actions. */
  onOpenOrder: (id: number) => void;
  onReorder: (order: LegacyOrder) => void;
  onCancel: (order: LegacyOrder) => void;
  onDuplicate: (order: LegacyOrder) => void;
  onSettleDebt: (order: LegacyOrder) => void;
  /**
   * Opens the page-level receipt lightbox — page-level because the overlay
   * belongs above everything and the table is just one widget under it,
   * mirroring the old `setUploadedImages` prop drilled into the cells.
   */
  onShowReceipt: (paths: string[]) => void;
  /** TanStack visibility map; omitted keys mean "visible". */
  columnVisibility?: VisibilityState;
  className?: string | undefined;
}

export function LegacyOrdersTable({
  orders,
  isLoading,
  isFetching,
  onOpenOrder,
  onReorder,
  onCancel,
  onDuplicate,
  onSettleDebt,
  onShowReceipt,
  columnVisibility = {},
  className,
}: LegacyOrdersTableProps) {
  const { t } = useTranslation('legacy');

  const columns = useMemo(
    () => [
      columnHelper.accessor('display_id', {
        id: 'id',
        header: () => t('table.id'),
        cell: (ctx) => (
          <LegacyIdCell
            displayId={ctx.getValue()}
            onCopied={() => toast.success(t('toast.copied'))}
          />
        ),
      }),
      columnHelper.accessor('vendor_order_id', {
        id: 'vendor_order_id',
        header: () => t('table.vendor_order_id'),
        cell: (ctx) => (
          <LegacyVendorOrderCell
            value={ctx.getValue()}
            onCopied={() => toast.success(t('toast.copied'))}
          />
        ),
      }),
      columnHelper.accessor('stage', {
        id: 'status',
        header: () => t('table.status'),
        // The old list badge is a SOLID stage-color pill with white 10px/700
        // text, and is itself a click target for the drawer.
        cell: (ctx) => (
          <LegacyStatusBadge
            stage={ctx.getValue()}
            solid
            size="sm"
            textColor="var(--lg-surface)"
            onClick={() => onOpenOrder(ctx.row.original.id)}
          />
        ),
      }),
      columnHelper.display({
        id: 'route',
        header: () => t('table.route'),
        cell: (ctx) => (
          <LegacyRouteCell
            origin={ctx.row.original.origin_address}
            destination={ctx.row.original.destination_address}
          />
        ),
      }),
      columnHelper.accessor('total_delivery_price', {
        id: 'price',
        header: () => t('table.price'),
        cell: (ctx) => `${legacyThousands(ctx.getValue())} ${t('table.sum')}`,
      }),
      columnHelper.display({
        id: 'waiting_price',
        header: () => t('table.waiting_price'),
        cell: (ctx) => <LegacyWaitPriceCell order={ctx.row.original} />,
      }),
      columnHelper.accessor('total_product_price', {
        id: 'items',
        header: () => t('table.items'),
        // `thousandDivider(x, uzs) || "0" + uzs` in the source — the helper
        // always returns at least "0 сум", so the fallback never fired and a
        // zero renders as "0 сум" through the formatter alone.
        cell: (ctx) => `${legacyThousands(ctx.getValue())} ${t('uzs')}`,
      }),
      columnHelper.display({
        id: 'product_paid',
        header: () => t('table.product_paid'),
        cell: (ctx) => (
          <LegacyProductPaidCell order={ctx.row.original} onShowReceipt={onShowReceipt} />
        ),
      }),
      columnHelper.accessor('courier', {
        id: 'courier',
        header: () => t('table.courier'),
        cell: (ctx) => {
          const name = legacyCourierName(ctx.getValue());
          // No courier — or a courier record with no first name — renders the
          // grey "не определен" fallback, exactly as the source did.
          if (!name) {
            return (
              <span className="truncate text-[var(--lg-grey-400)]">
                {t('single.not_defined')}
              </span>
            );
          }
          // The link deliberately does NOT stop propagation: the old cell had
          // the drawer handler on the cell itself, so tapping the phone link
          // both dialled and opened the order.
          const phone = ctx.getValue()?.phone;
          return (
            <a
              // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an EMPTY phone must fall back too (the old `||`), not just null
              href={`tel:${phone || '+998000000000'}`}
              className="font-semibold whitespace-pre-line text-[var(--lg-fg)]"
            >
              {name}
            </a>
          );
        },
      }),
      columnHelper.accessor('created_at', {
        id: 'date',
        header: () => t('table.date'),
        // Time FIRST, then the date — `HH:mm, DD.MM.YYYY`.
        cell: (ctx) => legacyListDateTime(ctx.getValue()),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => t('table.actions'),
        cell: (ctx) => (
          <LegacyActionsCell
            order={ctx.row.original}
            onReorder={onReorder}
            onCancel={onCancel}
            onDuplicate={onDuplicate}
            onSettleDebt={onSettleDebt}
          />
        ),
      }),
    ],
    [t, onOpenOrder, onReorder, onCancel, onDuplicate, onSettleDebt, onShowReceipt],
  );

  const table = useReactTable({
    data: orders,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // No client-side paging: otherwise a new `data` array auto-resets the page
    // index, which re-renders, which makes a new array — an endless render loop.
    manualPagination: true,
    // Visibility is owned by the page (persisted per merchant); the table
    // never toggles it itself, so no updater is wired.
    state: { columnVisibility },
  });

  // The empty state is rendered INSIDE the table, not in place of it: the old
  // grid's `overlayNoRowsTemplate` is an empty span and the message comes from
  // an overlay layered over the grid body, so its column headers stay on screen
  // with no rows beneath them. Returning early instead — which this once did —
  // hid the headers entirely and made an empty filter look like a broken page.
  const isEmpty = !isLoading && orders.length === 0;

  return (
    // The grid scrolls horizontally as a unit; the page itself must never
    // scroll sideways, so the overflow is owned here. `relative` anchors the
    // fetch overlay below.
    // `lg-xscroll` keeps the horizontal bar VISIBLE at rest (it owns the
    // `overflow-x`). The grid is ~1960px of columns, so it overflows on
    // anything under a 2K monitor — FHD included — and macOS/Brave overlay
    // scrollbars only paint mid-gesture, which made the clipped "Действия"
    // column read as missing rather than as scrolled-off.
    <div
      className={cn(
        'lg-xscroll relative min-h-0 w-full flex-1 overflow-y-auto',
        className,
      )}
    >
      <table className="w-full table-fixed border-separate border-spacing-0">
        <colgroup>
          {table.getVisibleFlatColumns().map((col) => (
            <col key={col.id} style={{ width: `${COLUMN_WIDTHS[col.id] ?? 132}px` }} />
          ))}
        </colgroup>

        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="h-[var(--lg-header-h)]">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={[
                    'sticky top-0 z-10 bg-[var(--lg-bg)] px-[var(--lg-cell-pad-x)] text-left',
                    'text-[12px] font-medium text-[var(--lg-grey-600)]',
                    header.column.id === 'actions'
                      ? 'sticky top-0 right-0 z-20 bg-[var(--lg-bg)]'
                      : '',
                  ].join(' ')}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {isEmpty && (
            <tr>
              <td colSpan={table.getVisibleFlatColumns().length} className="p-0">
                {/* One heading, nothing else — the old overlay rendered
                    `table.empty_data` as a lone <h3> with an empty <p>. */}
                <div className="flex items-center justify-center py-24 text-center">
                  <p className="text-[16px] font-medium text-[var(--lg-grey-700)]">
                    {t('table.empty_data')}
                  </p>
                </div>
              </td>
            </tr>
          )}
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="group h-[var(--lg-row-h)]">
              {row.getVisibleCells().map((cell, index) => {
                const isFirst = index === 0;
                const isActions = cell.column.id === 'actions';
                // EVERY cell except the actions column opens the drawer — the
                // old grid attached its drawer handler per-cell, excluding the
                // actions cell. Interactive bits inside a cell (copy pills,
                // the receipt zoom, action buttons) stop propagation
                // themselves. NOTE the gate is on `cell.column.id`, the
                // EXPLICIT column id — an accessor key like `display_id`
                // matches nothing here (see the regression test).
                const opensOrder = !isActions;
                return (
                  <td
                    key={cell.id}
                    {...(opensOrder
                      ? { onClick: () => onOpenOrder(row.original.id) }
                      : {})}
                    className={[
                      // The row box is a 70px MINIMUM and rows ABUT (measured:
                      // tops at 271/341/411 on the reference). The white
                      // surface is inset 2px top and bottom, which is what
                      // reads as a gap between rows. Reproduced with a 2px
                      // page-colored border over the white cell background —
                      // unlike the old fixed-height inner box, this stretches
                      // with an auto-height row (long routes wrap), keeping
                      // the white strip uniform across every cell of the row.
                      'border-y-2 border-[var(--lg-bg)] bg-[var(--lg-surface)]',
                      'px-[var(--lg-cell-pad-x)] align-middle',
                      'text-[16px] font-medium text-[var(--lg-grey-600)]',
                      // Row hover darkens the strip, as AG Grid's row-hover
                      // did (`AgGridTable.tsx:270-300`).
                      'group-hover:bg-[var(--lg-grey-200)]',
                      isActions ? 'sticky right-0 z-[5] bg-[var(--lg-surface)]' : '',
                      opensOrder ? 'cursor-pointer select-none' : '',
                      // Only the first cell carries the row's rounded left
                      // edge — the row is a strip of white cells, exactly as
                      // AG Grid rendered it.
                      isFirst ? 'rounded-l-[var(--lg-row-radius)]' : '',
                    ].join(' ')}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* The old `LoadingBlurry`: a blur + faint white wash with a spinner,
          layered over the PREVIOUS rows on every fetch (the query keeps
          previous data, so there is always something under it). */}
      {isFetching && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[16px] bg-[color-mix(in_srgb,var(--lg-surface)_16%,transparent)] backdrop-blur-[5px]">
          <span
            role="status"
            aria-label={t('loading')}
            className="size-8 animate-spin rounded-full border-2 border-[var(--lg-grey-300)] border-t-[var(--lg-orange-500)]"
          />
        </div>
      )}
    </div>
  );
}
